import * as React from "react";

export type Breakpoint = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

const BREAKPOINTS: Record<Exclude<Breakpoint, "xs">, number> = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
};

function getBreakpoint(width: number): Breakpoint {
  if (width >= BREAKPOINTS["2xl"]) return "2xl";
  if (width >= BREAKPOINTS.xl) return "xl";
  if (width >= BREAKPOINTS.lg) return "lg";
  if (width >= BREAKPOINTS.md) return "md";
  if (width >= BREAKPOINTS.sm) return "sm";
  return "xs";
}

export function useMediaQuery(query: string): boolean {
  const getSnapshot = React.useCallback(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }

    return window.matchMedia(query).matches;
  }, [query]);

  const [matches, setMatches] = React.useState(getSnapshot);

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQueryList = window.matchMedia(query);
    const handleChange = () => setMatches(mediaQueryList.matches);

    handleChange();
    mediaQueryList.addEventListener?.("change", handleChange);

    return () => {
      mediaQueryList.removeEventListener?.("change", handleChange);
    };
  }, [query]);

  return matches;
}

export function useBreakpoint(): Breakpoint {
  const getSnapshot = React.useCallback(() => {
    if (typeof window === "undefined") {
      return "xs" satisfies Breakpoint;
    }

    return getBreakpoint(window.innerWidth);
  }, []);

  const [breakpoint, setBreakpoint] = React.useState<Breakpoint>(getSnapshot);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleResize = () => setBreakpoint(getBreakpoint(window.innerWidth));

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return breakpoint;
}

export function useIsMobile(): boolean {
  return useMediaQuery("(max-width: 767.98px)");
}
